const minor = (money) => Number(money?.amountMinor || 0);

/**
 * Normaliza enums de apresentação da API Breakage (domínio backend → contrato UI).
 * Não altera regras de negócio; só a projeção consumida pelo front.
 *
 * Backend (domínio) → Front (apresentação):
 * - eligibility: BLOCKED → INELIGIBLE
 * - agingCondition: ACTIVE → WITHIN_WINDOW
 * - originType: FOUND_IN → FUNDS_IN (STANDARD/UNKNOWN sem inferência)
 */
export const normalizeEligibility = (value) => {
  if (value === "BLOCKED") {
    return "INELIGIBLE";
  }
  if (value === "ELIGIBLE" || value === "INELIGIBLE") {
    return value;
  }
  return value || null;
};

export const normalizeAgingCondition = (value) => {
  if (value === "ACTIVE") {
    return "WITHIN_WINDOW";
  }
  if (value === "EXPIRED" || value === "WITHIN_WINDOW") {
    return value;
  }
  return value || null;
};

export const normalizeOriginType = (value) => {
  if (value === "FOUND_IN") {
    return "FUNDS_IN";
  }
  return value || null;
};

export const normalizeIneligibilityReason = (value) => {
  if (value === "FOUND_IN_EXEMPT") {
    return "FUNDS_IN_EXEMPT";
  }
  return value ?? null;
};

export const deriveConsumptionCondition = (lot) => {
  if (lot?.consumptionCondition) {
    return lot.consumptionCondition;
  }
  const original = minor(lot?.originalAmount);
  const remaining = minor(lot?.remainingAmount);
  const consumed = minor(lot?.consumedAmount);

  if (original <= 0 && remaining <= 0 && consumed <= 0) {
    return null;
  }
  if (remaining <= 0 && (consumed > 0 || original > 0)) {
    return "FULL";
  }
  if (consumed <= 0 && remaining === original) {
    return "NONE";
  }
  if (consumed > 0 && remaining > 0) {
    return "PARTIAL";
  }
  return null;
};

export const normalizeBalanceLot = (lot) => {
  if (!lot) {
    return null;
  }
  const participant = lot.participant || {
    kind: null,
    legalName: null,
    documentNumber: null,
  };
  return {
    ...lot,
    participant: {
      kind: participant.kind ?? null,
      legalName: participant.legalName ?? null,
      documentNumber: participant.documentNumber ?? null,
    },
    eligibility: normalizeEligibility(lot.eligibility),
    agingCondition: normalizeAgingCondition(lot.agingCondition),
    originType: normalizeOriginType(lot.originType),
    breakageIneligibilityReason: normalizeIneligibilityReason(
      lot.breakageIneligibilityReason
    ),
    consumptionCondition: deriveConsumptionCondition(lot),
  };
};

export const normalizeBalanceLotPage = (page) => {
  if (!page) {
    return {
      items: [],
      page: { limit: 0, hasMore: false, nextCursor: null },
    };
  }
  return {
    ...page,
    items: Array.isArray(page.items)
      ? page.items.map(normalizeBalanceLot)
      : [],
  };
};

export const isPlaceholderRedemptionValue = (value) => {
  if (value == null) {
    return true;
  }
  const v = String(value).trim().toLowerCase();
  if (!v) {
    return true;
  }
  return (
    v.startsWith("pending-") ||
    v === "pending" ||
    v === "todo" ||
    v === "placeholder"
  );
};

/**
 * Contrato POST /api/breakage/v1/redemptions (alyplus-backend-net).
 * Campo canônico: breakageOperationId (não breakageProcessId).
 * Backend rejeita membros extras (UnmappedMemberHandling.Disallow).
 */
export const isValidRedemptionRequest = (body = {}) => {
  const {
    breakageOperationId,
    destinationAccountId,
    destinationPixKey,
    destinationHolderDocument,
  } = body;
  if (
    isPlaceholderRedemptionValue(breakageOperationId) ||
    isPlaceholderRedemptionValue(destinationAccountId) ||
    isPlaceholderRedemptionValue(destinationPixKey) ||
    isPlaceholderRedemptionValue(destinationHolderDocument)
  ) {
    return false;
  }
  return Boolean(
    breakageOperationId && destinationAccountId && destinationPixKey && destinationHolderDocument
  );
};

/** Monta o body exatamente no shape aceito pelo backend. */
export const buildRedemptionRequestBody = ({
  breakageOperationId,
  destinationAccountId,
  destinationPixKey,
  destinationHolderDocument,
} = {}) => ({
  breakageOperationId,
  destinationAccountId,
  destinationPixKey,
  destinationHolderDocument,
});

/** Lê a chave retornada pela API da empresa autenticada sem registrar o valor. */
export const resolveOwnerCompanyPixKey = (company = {}) => {
  const key = typeof company?.pix_key === "string" ? company.pix_key.trim() : "";
  return key && key.length <= 255 ? key : null;
};

/** HTTP 2xx de aceitação ≠ liquidação financeira. */
export const isRedemptionAccepted = (status) =>
  status >= 200 && status < 300;

export const isRedemptionFinanciallySettled = (payload) => {
  const state = String(
    payload?.businessState || payload?.status || ""
  ).toUpperCase();
  return state === "REDEEMED" || state === "SETTLED" || state === "COMPLETED";
};
